import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateUserRequest, UpdateUserRequest } from '@vagusx/clients';
import type { SearchRequest } from '@vagusx/shared';
import { getUserClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

const defaultSearchRequest: SearchRequest = {
  queries: [],
  limit: 100,
  offset: 0,
};

/**
 * Hook to fetch and search users.
 */
export function useUsers(searchRequest?: SearchRequest) {
  const request = searchRequest ?? defaultSearchRequest;
  return useQuery({
    queryKey: queryKeys.users.list(request),
    queryFn: () => getUserClient().search(request),
  });
}

/**
 * Hook to fetch a single user by ID.
 */
export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.detail(id ?? ''),
    queryFn: () => getUserClient().getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a user.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRequest) => getUserClient().create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

/**
 * Hook to update a user.
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      getUserClient().update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

/**
 * Hook to fetch inactive (deactivated) users.
 */
export function useInactiveUsers() {
  return useQuery({
    queryKey: queryKeys.users.inactive(),
    queryFn: () => getUserClient().listInactive(),
  });
}

/**
 * Hook to delete a user.
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getUserClient().deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

/**
 * Hook to reactivate a deactivated user.
 */
export function useReactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getUserClient().reactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}
